import { useState } from "react";

import { loadLocale } from "@smart/itops-locale-dom";
import { Alert } from "@smart/itops-sb-design-system-dom";

type InfoAlertProps = {
  isFormActive: boolean;
  hasRequiredField: boolean;
  clientRole: string;
  toggleFormActive: () => Promise<void>;
  setupNameFields: () => Promise<void>;
  hasFormLinks: boolean;
  hasDigitalSignatureField: boolean;
};

const buildDigitalSignatureNote = () => {
  const { terms } = loadLocale();
  return `This form has a digital signature field. Please change the signature field to an electronic signature before creating a web ${terms.enquiry.name} link.`;
};

const DigitalSignatureNote = ({ note }: { note?: string }) =>
  note ? <p className="text-caption">{note}</p> : null;

const DigitalSignatureOnlyAlert = ({ note }: { note: string }) => (
  <Alert
    heading="Shareable links and QR codes are unavailable"
    variant="section"
    palette="default"
    content={<p>{note}</p>}
  />
);

const MissingNameFieldsAlert = ({
  handleSetupNameFields,
  isSettingUpNameFields,
  digitalSignatureNote,
}: {
  handleSetupNameFields: () => Promise<void>;
  isSettingUpNameFields: boolean;
  digitalSignatureNote?: string;
}) => (
  <Alert
    heading="Name Fields are Required"
    variant="section"
    palette="warning"
    content={
      <div className="flex flex-col gap-medium">
        <p>
          To enable automatic lead creation, this form must include a required
          name field.
        </p>
        <p>
          If a prospect does not provide a name, a notification will be
          available upon submission, and can be reviewed in the Intake
          Dashboard. The prospect&apos;s details will need to be manually
          entered to create the lead.
        </p>
        <div className="text-caption">
          Note: When &quot;Set up name fields&quot; is selected, we&apos;ll make
          the existing name field required or add new ones to the start of the
          form.
        </div>
        <DigitalSignatureNote note={digitalSignatureNote} />
      </div>
    }
    actions={[
      ...(isSettingUpNameFields
        ? []
        : [
            {
              text: "Set up name fields",
              onClick: handleSetupNameFields,
            },
          ]),
    ]}
  />
);

const InactiveFormAlert = ({
  toggleFormActive,
  isActivatingForm,
  digitalSignatureNote,
}: {
  toggleFormActive: () => Promise<void>;
  isActivatingForm: boolean;
  digitalSignatureNote?: string;
}) => (
  <Alert
    heading="Shareable links and QR codes are unavailable"
    variant="section"
    palette="warning"
    content={
      <div className="flex flex-col gap-medium">
        <p className="m-0">
          This form is currently inactive. Any links, QR codes, and web
          enquiries will not work. To restore access, set the form to Active.
        </p>
        <DigitalSignatureNote note={digitalSignatureNote} />
      </div>
    }
    actions={[
      ...(isActivatingForm
        ? []
        : [
            {
              text: "Set to Active",
              onClick: toggleFormActive,
            },
          ]),
    ]}
  />
);

const MissingNameFieldsAndInactiveFormAlert = ({
  toggleFormActive,
  handleSetupNameFields,
  isSettingUpNameFields,
  isActivatingForm,
  digitalSignatureNote,
}: {
  toggleFormActive: () => Promise<void>;
  handleSetupNameFields: () => Promise<void>;
  isSettingUpNameFields: boolean;
  isActivatingForm: boolean;
  digitalSignatureNote?: string;
}) => (
  <Alert
    heading="Shareable links, QR codes and web enquiries are unavailable"
    variant="section"
    palette="warning"
    content={
      <div className="flex flex-col gap-medium">
        <p>
          This form can&apos;t accept web enquiries because it is inactive and
          missing required name fields. Set the form to Active and add required
          name fields to restore access.
        </p>
        <p className="text-caption">
          Note: When &quot;Set up name fields&quot; is selected, we&apos;ll make
          the existing name field required or add new ones to the start of the
          form.
        </p>
        <DigitalSignatureNote note={digitalSignatureNote} />
      </div>
    }
    actions={[
      ...(isActivatingForm
        ? []
        : [
            {
              text: "Set to Active",
              onClick: toggleFormActive,
            },
          ]),
      ...(isSettingUpNameFields
        ? []
        : [
            {
              text: "Set up name fields",
              onClick: handleSetupNameFields,
            },
          ]),
    ]}
  />
);

export const InfoAlert = ({
  isFormActive,
  hasRequiredField,
  clientRole,
  toggleFormActive,
  setupNameFields,
  hasFormLinks,
  hasDigitalSignatureField,
}: InfoAlertProps) => {
  const [isSettingUpNameFields, setIsSettingUpNameFields] = useState(false);
  const [isActivatingForm, setIsActivatingForm] = useState(false);

  const handleToggleFormActive = async () => {
    setIsActivatingForm(true);
    try {
      await toggleFormActive();
    } catch (error) {
      console.error("Error activating form", error);
    } finally {
      setIsActivatingForm(false);
    }
  };

  const handleSetupNameFields = async () => {
    setIsSettingUpNameFields(true);
    try {
      await setupNameFields();
    } catch (error) {
      console.error("Error setting up name fields", error);
    } finally {
      setIsSettingUpNameFields(false);
    }
  };

  const digitalSignatureNote = hasDigitalSignatureField
    ? buildDigitalSignatureNote()
    : undefined;

  const canCreateLink =
    isFormActive && hasRequiredField && !hasDigitalSignatureField;

  if (canCreateLink) return null;

  if (hasFormLinks) {
    if (!hasRequiredField && !isFormActive)
      return (
        <MissingNameFieldsAndInactiveFormAlert
          toggleFormActive={handleToggleFormActive}
          handleSetupNameFields={handleSetupNameFields}
          isSettingUpNameFields={isSettingUpNameFields}
          isActivatingForm={isActivatingForm}
          digitalSignatureNote={digitalSignatureNote}
        />
      );
    if (!hasRequiredField)
      return (
        <MissingNameFieldsAlert
          handleSetupNameFields={handleSetupNameFields}
          isSettingUpNameFields={isSettingUpNameFields}
          digitalSignatureNote={digitalSignatureNote}
        />
      );
    if (!isFormActive)
      return (
        <InactiveFormAlert
          toggleFormActive={handleToggleFormActive}
          isActivatingForm={isActivatingForm}
          digitalSignatureNote={digitalSignatureNote}
        />
      );
    if (digitalSignatureNote)
      return <DigitalSignatureOnlyAlert note={digitalSignatureNote} />;
  }

  return (
    <Alert
      variant="section"
      palette="default"
      content={
        <div className="flex flex-col gap-medium">
          <div className="text-ui">
            <p>Before a shareable link can be created for this form:</p>
            <ul>
              {!isFormActive && !isActivatingForm && (
                <li className="list-disc list-inside">
                  The form status must be set to Active.
                </li>
              )}
              {!hasRequiredField && !isSettingUpNameFields && (
                <li className="list-disc list-inside">
                  A {clientRole} name field must be marked as required.
                </li>
              )}
              {hasDigitalSignatureField && (
                <li className="list-disc list-inside">
                  The digital signature component must be changed to an
                  electronic signature.
                </li>
              )}
            </ul>
          </div>
          {!hasRequiredField && !isSettingUpNameFields && (
            <div className="text-caption">
              Note: When &quot;Set up name fields&quot; is selected, we&apos;ll
              make the existing name field required or add new ones to the start
              of the form.
            </div>
          )}
        </div>
      }
      actions={[
        ...(isFormActive || isActivatingForm
          ? []
          : [
              {
                text: "Set to Active",
                onClick: handleToggleFormActive,
              },
            ]),
        ...(hasRequiredField || isSettingUpNameFields
          ? []
          : [
              {
                text: "Set up name fields",
                onClick: handleSetupNameFields,
              },
            ]),
      ]}
    />
  );
};
